/**
 * Error message mapper for user-friendly authentication error messages
 */

export const MISSING_ACCOUNT_MESSAGE = "No account was found for this email.";

const GENERIC_CREDENTIALS_MESSAGE =
  "The email or password you entered is incorrect.";

export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  // Wrong-password style errors stay generic. A missing account is handled separately.
  "Invalid credentials": GENERIC_CREDENTIALS_MESSAGE,
  "Invalid email or password": GENERIC_CREDENTIALS_MESSAGE,
  "Incorrect password": GENERIC_CREDENTIALS_MESSAGE,
  "Wrong password": GENERIC_CREDENTIALS_MESSAGE,
  "Password is incorrect": GENERIC_CREDENTIALS_MESSAGE,
  "Invalid password": GENERIC_CREDENTIALS_MESSAGE,
  // NextAuth default errors
  CredentialsSignin: "The email or password you entered is incorrect.",
  Signin: "The email or password you entered is incorrect.",
  // API error fallbacks
  "API error": GENERIC_CREDENTIALS_MESSAGE,

  // Account status errors
  "Account is disabled":
    "Your account has been disabled. Please contact support.",
  "Account is locked":
    "Your account has been temporarily locked. Please try again later.",
  AccountLockedOut:
    "Too many failed sign-in attempts. Your account is locked for 15 minutes. Please try again later.",
  Locked:
    "Too many failed sign-in attempts. Your account is locked for 15 minutes. Please try again later.",
  "Account not verified": "Please verify your email address before signing in.",

  // Network/Server errors
  "Network error":
    "Unable to connect to the server. Please check your internet connection.",
  "Server error": "Something went wrong on our end. Please try again later.",
  "Request timeout": "The request took too long. Please try again.",

  // Admin specific
  "You do not have admin privileges": "You do not have admin privileges.",
  "Access Denied": "You do not have permission to access this area.",

  // Rate limiting
  "Too many attempts":
    "Too many login attempts. Please try again in a few minutes.",
  "Rate limit exceeded":
    "Too many requests. Please try again in a few minutes.",
};

function isMissingAccountError(error: string): boolean {
  const lower = error.toLowerCase();
  if (/user\.not[_-]?found/.test(lower)) return true;
  if (lower.includes("no user") || lower.includes("unknown user")) return true;

  const mentionsIdentity =
    lower.includes("user") ||
    lower.includes("email") ||
    lower.includes("account");
  const saysMissing =
    lower.includes("not found") ||
    lower.includes("does not exist") ||
    lower.includes("doesn't exist") ||
    lower.includes("doesnt exist") ||
    lower.includes("not exist") ||
    lower.includes("no account") ||
    lower.includes("not registered");

  return mentionsIdentity && saysMissing;
}

function missingAccountDisplayMessage(error: string): string {
  const trimmed = error.trim();
  if (/^[A-Za-z0-9_.-]+$/.test(trimmed)) return MISSING_ACCOUNT_MESSAGE;
  return trimmed;
}

/**
 * Maps raw error messages to user-friendly messages
 * @param error - The raw error message from the API
 * @returns User-friendly error message
 */
export function getAuthErrorMessage(error: string | null | undefined): string {
  if (!error) {
    return "An unexpected error occurred. Please try again.";
  }

  if (isMissingAccountError(error)) {
    return missingAccountDisplayMessage(error);
  }

  // Check for exact match
  if (AUTH_ERROR_MESSAGES[error]) {
    return AUTH_ERROR_MESSAGES[error];
  }

  const errorLower = error.toLowerCase();

  // Lockout after repeated failed logins (HTTP 423) - before generic "account" masking
  if (
    errorLower.includes("accountlockedout") ||
    errorLower.includes("locked out") ||
    (errorLower.includes("locked") && !errorLower.includes("blocked"))
  ) {
    return AUTH_ERROR_MESSAGES.AccountLockedOut;
  }

  // Check for partial matches (case-insensitive)
  for (const [key, message] of Object.entries(AUTH_ERROR_MESSAGES)) {
    if (errorLower.includes(key.toLowerCase())) {
      return message;
    }
  }

  // Check for common patterns - use generic message for security
  if (errorLower.includes("password") || errorLower.includes("incorrect")) {
    return GENERIC_CREDENTIALS_MESSAGE;
  }

  if (
    errorLower.includes("email") ||
    errorLower.includes("user") ||
    errorLower.includes("not found") ||
    errorLower.includes("account")
  ) {
    return GENERIC_CREDENTIALS_MESSAGE;
  }

  if (errorLower.includes("network") || errorLower.includes("fetch")) {
    return "Unable to connect to the server. Please check your internet connection.";
  }

  // For any unrecognized authentication-related errors, use generic message for security
  // This prevents information leakage about whether an email exists or not
  if (
    errorLower.includes("login") ||
    errorLower.includes("auth") ||
    errorLower.includes("signin") ||
    errorLower.includes("credential")
  ) {
    return GENERIC_CREDENTIALS_MESSAGE;
  }

  // Return original error if no match found (fallback for non-auth errors)
  return error;
}

/**
 * Maps sign-up errors to user-friendly messages.
 * Registration errors (e.g. duplicate email) should show the API message as-is.
 */
export function getSignUpErrorMessage(
  error: string | null | undefined,
): string {
  if (!error) {
    return "An unexpected error occurred. Please try again.";
  }

  const errorLower = error.toLowerCase();
  if (
    errorLower.includes("already taken") ||
    error.includes("EmailAlreadyTaken") ||
    error.includes("User.EmailAlreadyTaken")
  ) {
    return error;
  }

  return getAuthErrorMessage(error);
}
